import React, { useEffect, useState } from 'react';

import Avatar from './Avatar';
import { previewFor } from './messageContent';
import type { PhoneArrival } from './usePhoneMessages';

export interface Props {
  arrival: PhoneArrival;
  onOpen: () => void;
  onDismiss: () => void;
}

const SHOW_MS = 7000;

/** The one line a new message is announced and previewed with. */
export function arrivalLine({ message }: PhoneArrival): string {
  return message.delivery.presentation === 'attachment'
    ? message.delivery.attachmentLabel?.trim() || message.point.title
    : previewFor(message.point.pages?.[0]);
}

/** "New message" sliding down over whatever the player is looking at. */
const PhoneBanner: React.FC<Props> = ({ arrival, onOpen, onDismiss }) => {
  // Someone reading it, or tabbed onto it, keeps it on screen.
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (held) return;
    const timer = setTimeout(onDismiss, SHOW_MS);
    return () => clearTimeout(timer);
    // Keyed on the arrival so a second message restarts the clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrival.id, held]);

  const { message } = arrival;
  const line = arrivalLine(arrival);

  return (
    <div
      className="Phone_banner"
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <button type="button" className="Phone_bannerBody" onClick={onOpen}>
        <Avatar
          name={message.sender.name}
          src={message.sender.avatar}
          size="sm"
        />
        <span className="Phone_bannerText">
          <strong>{message.sender.name}</strong>
          <span>{line || 'New message'}</span>
        </span>
      </button>
      <button
        type="button"
        className="Phone_bannerClose"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path
            d="m5.5 5.5 9 9m0-9-9 9"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default PhoneBanner;
